// ...existing code...
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  Search, 
  Edit, 
  RefreshCw, 
  Eye, 
  EyeOff, 
  AlertCircle, 
  CheckCircle, 
  Shield, 
  UserPlus,
  Key,
  Lock,
  Unlock,
  Info
} from 'lucide-react';
import api from '../../services/api';
import usePermissions from '../../hooks/usePermissions';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';

const UserManagement = () => {
  const { hasPermission, getPermissionLevel } = usePermissions();
  
  // Permission checks for user-management page
  const canCreate = hasPermission('user-management', 'create');
  const canEdit = hasPermission('user-management', 'edit');
  // canDelete removed - delete functionality not implemented
  const permissionLevel = getPermissionLevel('user-management');
  
  // Helper functions to transform between frontend camelCase and backend snake_case
  const transformUserFromBackend = (user) => {
    return {
      ...user,
      firstName: user.first_name,
      lastName: user.last_name,
      fullName: `${user.first_name} ${user.last_name}`.trim()
    };
  };

  const transformUserToBackend = (user) => {
    return {
      first_name: user.firstName,
      last_name: user.lastName,
      username: user.username,
      email: user.email,
      role: user.role
    };
  };
  
  const [users, setUsers] = useState([]);
  // const [loading, setLoading] = useState(true); // Not used in current implementation
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(null);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    role: 'staff'
  });
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    useDefault: true
  });
  const [showPassword, setShowPassword] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      // setLoading(true); // Loading not used in current implementation
      const data = await api.getUsers();
      const transformedUsers = Array.isArray(data) ? data.map(transformUserFromBackend) : [];
      setUsers(transformedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Network error while fetching users');
      setUsers([]); // Ensure users is always an array
    } finally {
      // setLoading(false); // Loading not used in current implementation
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const validatePassword = (password) => {
    const validations = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    
    return {
      valid: Object.values(validations).every(Boolean),
      checks: {
        'At least 8 characters': validations.length,
        'One uppercase letter': validations.uppercase,
        'One lowercase letter': validations.lowercase,
        'One number': validations.number,
        'One special character': validations.special
      }
    };
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Transform frontend camelCase to backend snake_case and add default password
      const userDataForBackend = {
        first_name: newUser.firstName,
        last_name: newUser.lastName,
        username: newUser.username,
        email: newUser.email,
        role: newUser.role,
        password: 'User@123' // Default password as mentioned in UI
      };
      
      const createdUser = await api.createUser(userDataForBackend);
      const transformedUser = transformUserFromBackend(createdUser);
      setUsers([...users, transformedUser]);
      setShowCreateForm(false);
      setNewUser({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        role: 'staff'
      });
      setError(''); // Clear any previous errors
    } catch (error) {
      console.error('Error creating user:', error);
      setError('Failed to create user: ' + error.message);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const updatedUser = await api.updateUser(userId, updates);
      const transformedUser = transformUserFromBackend(updatedUser);
      setUsers(users.map(user => user.id === userId ? transformedUser : user));
      setEditingUser(null);
      setError('');
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user: ' + error.message);
    }
  };

  // const handleConfirmDelete = async (userId) => { // Not used in current implementation
  //   try {
  //     await api.deleteUser(userId);
  //     setUsers(users.filter(user => user.id !== userId));
  //     setError('');
  //   } catch (error) {
  //     console.error('Error deleting user:', error);
  //     setError('Failed to delete user: ' + error.message);
  //   }
  // };

  const handleResetPassword = async (userId) => {
    try {
      const body = resetPasswordData.useDefault ? {} : { newPassword: resetPasswordData.newPassword };
      
      const response = await fetch(`/api/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        setShowPasswordReset(null);
        setResetPasswordData({ newPassword: '', useDefault: true });
        setError('');
        
        const passwordMessage = data.temporaryPassword 
          ? `Password reset successfully! Temporary password: ${data.temporaryPassword}`
          : 'Password reset successfully!';
        
        alert(`${passwordMessage}\nUser must change password on next login.`);
        
        // Refresh users to update status
        fetchUsers();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Network error while resetting password');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = (
      user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-red-100 text-red-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'staff': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRole = (role) => {
    return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Render UI
  return (
    <div className="p-6 bg-white rounded-lg shadow-sm pb-12">
      <PageIdentifier pageId={SCREEN_IDS?.ADMINISTRATION?.USER_MANAGEMENT || 'ADM-001'} pageName="User Management" />
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Users className="mr-2" />
            User Management
          </h2>
          <p className="text-gray-600 mt-1">Manage user accounts and permissions</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          disabled={!canCreate}
          className={`px-4 py-2 rounded-md flex items-center ${
            canCreate 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          title={!canCreate ? `No permission to create users (Level: ${permissionLevel})` : ''}
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md flex items-center">
          <AlertCircle className="w-4 h-4 mr-2" />
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            />
          </div>
        </div>
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="staff">Staff</option>
        </select>
        <button
          onClick={fetchUsers}
          className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 flex items-center"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Users Table */}
      <div className="overflow-x-auto">
        <table className="w-full table-auto">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{user.fullName}</div>
                    <div className="text-sm text-gray-500">@{user.username}</div>
                    {user.email && <div className="text-sm text-gray-500">{user.email}</div>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                    <Shield className="w-3 h-3 mr-1" />
                    {formatRole(user.role)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col space-y-1">
                    {user.isLocked ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <Lock className="w-3 h-3 mr-1" />
                        Locked
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Unlock className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    )}
                    {user.is_password_reset_required && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <Key className="w-3 h-3 mr-1" />
                        Password Reset Required
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingUser(user)}
                      disabled={!canEdit}
                      className={`${
                        canEdit 
                          ? 'text-blue-600 hover:text-blue-900' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                      title={!canEdit ? `No permission to edit users (Level: ${permissionLevel})` : 'Edit User'}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setShowPasswordReset(user.id)}
                      disabled={!canEdit}
                      className={`${
                        canEdit 
                          ? 'text-green-600 hover:text-green-900' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                      title={!canEdit ? `No permission to reset passwords (Level: ${permissionLevel})` : 'Reset Password'}
                    >
                      <Key className="w-4 h-4" />
                    </button>
                    {/* Delete button removed - handleDeleteUser function was removed */}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No users found matching your criteria.
        </div>
      )}

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">Create New User</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      placeholder="First Name"
                      value={newUser.firstName}
                      onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={newUser.lastName}
                      onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    placeholder="Username"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email (Optional)
                  </label>
                  <input
                    type="email"
                    placeholder="Email (Optional)"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
              </div>
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  id="role"
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <div className="bg-blue-50 p-3 rounded-md">
                <div className="flex items-start">
                  <Info className="w-4 h-4 text-blue-500 mr-2 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">Default Password: User@123</p>
                    <p>User will be required to change password on first login.</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
  {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">Edit User</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              // Transform frontend camelCase to backend snake_case
              const userDataForBackend = transformUserToBackend(editingUser);
              handleUpdateUser(editingUser.id, userDataForBackend);
            }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={editingUser.firstName}
                    onChange={(e) => setEditingUser({...editingUser, firstName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={editingUser.lastName}
                    onChange={(e) => setEditingUser({...editingUser, lastName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    placeholder="Username"
                    value={editingUser.username}
                    onChange={(e) => setEditingUser({...editingUser, username: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    placeholder="Email (Optional)"
                    value={editingUser.email || ''}
                    onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
              </div>
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  id="role"
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">Reset Password</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="radio"
                  id="useDefault"
                  checked={resetPasswordData.useDefault}
                  onChange={() => setResetPasswordData({...resetPasswordData, useDefault: true})}
                  className="h-4 w-4 text-blue-600"
                />
                <label htmlFor="useDefault" className="text-sm font-medium text-gray-700">
                  Use default password (User@123)
                </label>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="radio"
                  id="useCustom"
                  checked={!resetPasswordData.useDefault}
                  onChange={() => setResetPasswordData({...resetPasswordData, useDefault: false})}
                  className="h-4 w-4 text-blue-600"
                />
                <label htmlFor="useCustom" className="text-sm font-medium text-gray-700">
                  Set custom password
                </label>
              </div>
              
              {!resetPasswordData.useDefault && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={resetPasswordData.newPassword}
                      onChange={(e) => setResetPasswordData({...resetPasswordData, newPassword: e.target.value})}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required={!resetPasswordData.useDefault}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {resetPasswordData.newPassword && (
                    <div className="mt-2 bg-gray-50 p-3 rounded-md">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</h4>
                      <div className="space-y-1">
                        {Object.entries(validatePassword(resetPasswordData.newPassword).checks).map(([requirement, isValid]) => (
                          <div key={requirement} className="flex items-center text-xs">
                            {isValid ? (
                              <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                            ) : (
                              <AlertCircle className="w-3 h-3 text-red-500 mr-2" />
                            )}
                            <span className={isValid ? 'text-green-700' : 'text-red-700'}>
                              {requirement}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="bg-yellow-50 p-3 rounded-md">
                <div className="flex items-start">
                  <Info className="w-4 h-4 text-yellow-500 mr-2 mt-0.5" />
                  <div className="text-sm text-yellow-700">
                    <p>User will be required to change password on next login.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordReset(null);
                    setResetPasswordData({ newPassword: '', useDefault: true });
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleResetPassword(showPasswordReset)}
                  disabled={!resetPasswordData.useDefault && !validatePassword(resetPasswordData.newPassword).valid}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
